import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { setReportsList } from '@/store/diffsSlice';
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { supabase } from '@/integrations/supabase/client';
import type { RootState } from '@/store/store';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  FileText,
  ExternalLink,
  Plus,
  RefreshCw,
  CheckCircle,
  Clock,
  AlertCircle,
  Trash2,
  MoreVertical,
  XCircle
} from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { useToast } from "@/hooks/use-toast";

interface DiffsReportsTabProps {
  periodFilter: string;
}

interface Report {
  id: string;
  title: string;
  period: string;
  status: 'pending' | 'running' | 'completed' | 'failed';
  created_at: string;
  updated_at: string;
  task_id?: string;
  summary?: string;
  published?: boolean;
  published_at?: string;
  stats?: {
    totalDiffs: number;
    totalChanges: number;
    companies: number;
  };
}

export const DiffsReportsTab: React.FC<DiffsReportsTabProps> = ({ periodFilter }) => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const dispatch = useDispatch();

  // Get cached reports from Redux
  const cachedReports = useSelector((state: RootState) => state.diffs.reportsCache);

  const reports = cachedReports?.data || [];
  const [isLoading, setIsLoading] = useState(true);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [newReportTitle, setNewReportTitle] = useState('');
  const [newReportPrompt, setNewReportPrompt] = useState('');
  const [isCreating, setIsCreating] = useState(false);

  // Delete confirmation state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [reportToDelete, setReportToDelete] = useState<Report | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // Fetch reports from API
  useEffect(() => {
    const loadReports = async () => {
      const cacheKey = periodFilter || '';

      // Check if we have valid cached data
      const isCacheValid = cachedReports?.periodFilter === cacheKey;

      if (isCacheValid) {
        // Use cached data - instant load!
        console.log('[DiffsReportsTab] Using cached reports for period:', cacheKey);
        setIsLoading(false);
        return;
      }

      // Need to fetch fresh data
      console.log('[DiffsReportsTab] Fetching fresh reports for period:', cacheKey);
      setIsLoading(true);

      try {
        // Query database directly
        let query = supabase
          .from('app_diff_reports')
          .select('*')
          .order('created_at', { ascending: false })
          .limit(50);

        // Apply period filter if provided
        if (periodFilter) {
          query = query.eq('period', periodFilter);
        }

        const { data: dbReports, error: dbError } = await query;

        if (dbError) {
          throw dbError;
        }

        // Map database records to Report interface
        const mappedReports: Report[] = (dbReports || []).map(report => {
          // Generate a title from the period if not available
          const title = report.period.includes('W')
            ? `Weekly Pricing Changes - ${report.period}`
            : `Quarterly Pricing Analysis - ${report.period}`;

          return {
            id: report.id,
            title,
            period: report.period,
            status: 'completed', // Reports in DB are already completed
            created_at: report.created_at,
            updated_at: report.updated_at,
            published: report.published || false,
            published_at: report.published_at,
            summary: `Analysis of pricing changes${report.companies_count ? ` across ${report.companies_count} companies` : ''}`,
            stats: {
              totalDiffs: report.diffs_count || 0,
              totalChanges: report.events_count || 0,
              companies: report.companies_count || 0,
            }
          };
        });

        // Cache in Redux
        dispatch(setReportsList({
          reports: mappedReports,
          periodFilter: cacheKey,
        }));
      } catch (error) {
        console.error('Error loading reports:', error);
        toast({
          title: "Error",
          description: "Failed to load reports",
          variant: "destructive",
        });
      } finally {
        setIsLoading(false);
      }
    };

    loadReports();
  }, [periodFilter]);

  const handleCreateReport = async () => {
    if (!newReportTitle.trim()) {
      toast({
        title: "Validation Error",
        description: "Please enter a report title",
        variant: "destructive",
      });
      return;
    }

    if (!periodFilter) {
      toast({
        title: "Validation Error",
        description: "Please select a period filter before creating a report",
        variant: "destructive",
      });
      return;
    }

    setIsCreating(true);
    try {
      // 1. Create Claude task for analysis
      const { data: task, error: taskError } = await supabase
        .from('claude_tasks')
        .insert({
          command: 'analyze',
          slug: 'all',
          params: {
            period: periodFilter,
            customPrompt: newReportPrompt || undefined,
          },
          status: 'pending',
          priority: 3, // Set priority to 3 as requested
        })
        .select()
        .single();

      if (taskError || !task) {
        throw new Error(taskError?.message || 'Failed to create task');
      }

      // 2. Create report record in database with task_id
      const { data: newReport, error: reportError } = await supabase
        .from('app_diff_reports')
        .insert({
          period: periodFilter,
          task_id: task.id,
          companies_count: 0,
          diffs_count: 0,
          trends_count: 0,
          events_count: 0,
        })
        .select()
        .single();

      if (reportError || !newReport) {
        throw new Error(reportError?.message || 'Failed to create report');
      }

      // 3. Map to Report interface for display
      const title = periodFilter.includes('W')
        ? `Weekly Pricing Changes - ${periodFilter}`
        : `Quarterly Pricing Analysis - ${periodFilter}`;

      const mappedReport: Report = {
        id: newReport.id,
        title,
        period: newReport.period,
        status: 'pending',
        created_at: newReport.created_at,
        updated_at: newReport.updated_at,
        task_id: newReport.task_id,
        published: false,
        summary: 'Analysis in progress...',
        stats: {
          totalDiffs: 0,
          totalChanges: 0,
          companies: 0,
        }
      };

      // 4. Update Redux cache with new report
      dispatch(setReportsList({
        reports: [mappedReport, ...reports],
        periodFilter: periodFilter || '',
      }));

      toast({
        title: "Report Created",
        description: "Your report is being generated. Check back soon!",
      });

      setCreateDialogOpen(false);
      setNewReportTitle('');
      setNewReportPrompt('');
    } catch (error) {
      console.error('Error creating report:', error);
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to create report",
        variant: "destructive",
      });
    } finally {
      setIsCreating(false);
    }
  };

  const handleViewReport = (reportId: string) => {
    navigate(`/diffs/reports/${reportId}`);
  };

  const handleViewTask = (taskId: string) => {
    navigate(`/tasks/${taskId}`);
  };

  const handleDeleteClick = (report: Report, e: React.MouseEvent) => {
    e.stopPropagation();
    setReportToDelete(report);
    setDeleteDialogOpen(true);
  };

  const handleUnpublishClick = async (report: Report, e: React.MouseEvent) => {
    e.stopPropagation();

    try {
      const { error } = await supabase
        .from('app_diff_reports')
        .update({
          published: false,
          published_url: null,
          published_at: null
        })
        .eq('id', report.id);

      if (error) throw error;

      // Update Redux cache - update the report to unpublished
      dispatch(setReportsList({
        reports: reports.map(r =>
          r.id === report.id
            ? { ...r, published: false, published_url: undefined, published_at: undefined }
            : r
        ),
        periodFilter: periodFilter || '',
      }));

      toast({
        title: "Report Unpublished",
        description: "The report has been unpublished successfully",
      });
    } catch (error) {
      console.error('Error unpublishing report:', error);
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to unpublish report",
        variant: "destructive",
      });
    }
  };

  const handleDeleteConfirm = async () => {
    if (!reportToDelete) return;

    setIsDeleting(true);
    try {
      const { error } = await supabase
        .from('app_diff_reports')
        .delete()
        .eq('id', reportToDelete.id);

      if (error) throw error;

      // Update Redux cache - remove the deleted report
      dispatch(setReportsList({
        reports: reports.filter(r => r.id !== reportToDelete.id),
        periodFilter: periodFilter || '',
      }));

      toast({
        title: "Report Deleted",
        description: "The report has been deleted successfully",
      });

      setDeleteDialogOpen(false);
      setReportToDelete(null);
    } catch (error) {
      console.error('Error deleting report:', error);
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to delete report",
        variant: "destructive",
      });
    } finally {
      setIsDeleting(false);
    }
  };

  const getStatusBadge = (status: string) => {
    const variants: Record<string, { color: string; icon: React.ReactNode }> = {
      completed: { color: 'bg-green-100 text-green-800', icon: <CheckCircle className="h-3 w-3" /> },
      running: { color: 'bg-blue-100 text-blue-800', icon: <RefreshCw className="h-3 w-3 animate-spin" /> },
      pending: { color: 'bg-yellow-100 text-yellow-800', icon: <Clock className="h-3 w-3" /> },
      failed: { color: 'bg-red-100 text-red-800', icon: <AlertCircle className="h-3 w-3" /> },
    };

    const variant = variants[status] || variants.pending;
    return (
      <Badge variant="outline" className={`${variant.color} gap-1`}>
        {variant.icon}
        {status}
      </Badge>
    );
  };

  const formatRelativeTime = (dateString: string) => {
    try {
      return formatDistanceToNow(new Date(dateString), { addSuffix: true });
    } catch {
      return dateString;
    }
  };

  return (
    <div className="space-y-6">
      {/* Header with Create Button */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">
            Analysis Reports
            {periodFilter && (
              <span className="ml-2 text-sm font-normal text-gray-500">
                for {periodFilter}
              </span>
            )}
          </h2>
          <p className="text-sm text-gray-600 mt-1">
            AI-generated analysis reports for pricing changes
          </p>
        </div>

        <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
          <DialogTrigger asChild>
            <Button className="gap-2">
              <Plus className="h-4 w-4" />
              Create Report
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Create New Analysis Report</DialogTitle>
              <DialogDescription>
                Generate an AI analysis report for pricing changes
                {periodFilter && ` in ${periodFilter}`}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <label className="text-sm font-medium">Report Title</label>
                <Input
                  value={newReportTitle}
                  onChange={(e) => setNewReportTitle(e.target.value)}
                  placeholder="e.g., Weekly Pricing Changes - 2025W47"
                />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium">Analysis Prompt (Optional)</label>
                <Textarea
                  value={newReportPrompt}
                  onChange={(e) => setNewReportPrompt(e.target.value)}
                  placeholder="Describe what you want to analyze or leave blank for default analysis"
                  rows={4}
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setCreateDialogOpen(false)}>
                Cancel
              </Button>
              <Button onClick={handleCreateReport} disabled={isCreating}>
                {isCreating ? 'Creating...' : 'Create Report'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {/* Reports Table */}
      <Card>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="p-8 text-center text-gray-500">
              <RefreshCw className="h-12 w-12 mx-auto mb-3 text-gray-400 animate-spin" />
              <p className="text-sm">Loading reports...</p>
            </div>
          ) : reports.length === 0 ? (
            <div className="p-8 text-center text-gray-500">
              <FileText className="h-12 w-12 mx-auto mb-3 text-gray-400" />
              <p className="text-sm">No reports found{periodFilter && ` for ${periodFilter}`}</p>
              <Button
                variant="outline"
                size="sm"
                className="mt-4"
                onClick={() => setCreateDialogOpen(true)}
              >
                <Plus className="h-4 w-4 mr-2" />
                Create Your First Report
              </Button>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Report Title</TableHead>
                  <TableHead>Period</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Stats</TableHead>
                  <TableHead>Created</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {reports.map((report) => (
                  <TableRow
                    key={report.id}
                    className="cursor-pointer hover:bg-gray-50"
                    onClick={() => handleViewReport(report.id)}
                  >
                    <TableCell className="font-medium">{report.title}</TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Badge variant="outline">{report.period}</Badge>
                        {report.published && (
                          <Badge className="bg-blue-100 text-blue-800 border-blue-200">
                            Published
                          </Badge>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>{getStatusBadge(report.status)}</TableCell>
                    <TableCell>
                      {report.stats ? (
                        <div className="text-xs text-gray-600">
                          {report.stats.companies} companies, {report.stats.totalDiffs} diffs
                        </div>
                      ) : (
                        <span className="text-xs text-gray-400">-</span>
                      )}
                    </TableCell>
                    <TableCell className="text-sm text-gray-600">
                      {formatRelativeTime(report.created_at)}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-2">
                        {report.task_id && report.status !== 'completed' && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleViewTask(report.task_id!);
                            }}
                          >
                            <ExternalLink className="h-4 w-4" />
                          </Button>
                        )}
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={(e) => e.stopPropagation()}
                            >
                              <MoreVertical className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            {report.published ? (
                              <DropdownMenuItem
                                onClick={(e) => handleUnpublishClick(report, e)}
                                className="text-orange-600 focus:text-orange-600"
                              >
                                <XCircle className="h-4 w-4 mr-2" />
                                Unpublish
                              </DropdownMenuItem>
                            ) : (
                              <DropdownMenuItem
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDeleteClick(report, e);
                                }}
                                className="text-red-600 focus:text-red-600"
                              >
                                <Trash2 className="h-4 w-4 mr-2" />
                                Delete Report
                              </DropdownMenuItem>
                            )}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Report?</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete this report? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          {reportToDelete && (
            <div className="py-4">
              <p className="text-sm font-medium text-gray-900">{reportToDelete.title}</p>
              <p className="text-sm text-gray-600 mt-1">Period: {reportToDelete.period}</p>
              <p className="text-sm text-gray-600">Created: {formatRelativeTime(reportToDelete.created_at)}</p>
            </div>
          )}
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setDeleteDialogOpen(false)}
              disabled={isDeleting}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleDeleteConfirm}
              disabled={isDeleting}
            >
              {isDeleting ? 'Deleting...' : 'Delete Report'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
